import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDoctorContext } from "@contexts";

export interface MissingDoctorRequirements {
  fea: boolean;
  address: boolean;
}

interface UseDoctorDocumentReadinessOptions {
  requestId?: string;
  redirectTo?: string;
}

export interface DoctorDocumentReadinessModalProps {
  missing: MissingDoctorRequirements | null;
  onClose: () => void;
  requestId?: string;
  redirectTo: string;
}

export const useDoctorDocumentReadiness = (
  options: UseDoctorDocumentReadinessOptions = {},
) => {
  const doctor = useDoctorContext();
  const location = useLocation();
  const [missing, setMissing] = useState<MissingDoctorRequirements | null>(null);

  const ensureReady = useCallback(
    (onReady: () => void) => {
      const needsFea = !doctor.isFeaAuthenticated;
      const needsAddress = !doctor.region || !doctor.commune || !doctor.street;
      if (needsFea || needsAddress) {
        setMissing({ fea: needsFea, address: needsAddress });
        return false;
      }
      onReady();
      return true;
    },
    [
      doctor.isFeaAuthenticated,
      doctor.region,
      doctor.commune,
      doctor.street,
    ],
  );

  const onClose = useCallback(() => setMissing(null), []);

  const modalProps: DoctorDocumentReadinessModalProps = useMemo(
    () => ({
      missing,
      onClose,
      requestId: options.requestId,
      redirectTo: options.redirectTo ?? location.pathname,
    }),
    [missing, onClose, options.requestId, options.redirectTo, location.pathname],
  );

  return { ensureReady, modalProps };
};
